import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Hexagon, Play, MapPin, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { BentoCard, BrutalButton } from "@/components/dashboard/BentoCard";
import { HexBadge } from "@/components/dashboard/Hex";
import { SOURCES } from "@/constants/testIds";

export default function SourcesPage() {
  const qc = useQueryClient();

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ["sources"],
    queryFn: async () => (await api.get("/sources")).data,
  });

  const runOne = useMutation({
    mutationFn: async (source_id) =>
      (await api.post("/scrape/run", source_id ? { source_id } : {})).data,
    onSuccess: (data) => {
      if (data.queued) {
        toast.success(
          data.message ||
          "Scrape started in the background. Check the Runs page in ~2 minutes.",
        );
        window.setTimeout(() => qc.invalidateQueries(), 120_000);
      } else {
        toast.success(`Scraped ${data.property_count} records (${data.ok_count}/${data.source_count} ok).`);
        qc.invalidateQueries();
      }
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err.message || "Scrape failed";
      toast.error(msg);
    },
  });

  return (
    <div data-testid={SOURCES.root} className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="data-label">Configured</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Sources</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            One cell per public records source. Toggle via <code className="font-mono-data">config/sources.yaml</code>.
          </p>
        </div>
        <BrutalButton
          data-testid={SOURCES.runAllBtn}
          onClick={() => runOne.mutate(null)}
          disabled={runOne.isPending}
        >
          <Play size={16} strokeWidth={2.5} />
          {runOne.isPending ? "Scraping…" : "Run All"}
        </BrutalButton>
      </div>

      {isLoading ? (
        <p className="text-zinc-500">Loading sources…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources.map((src) => (
            <SourceCard key={src.id} src={src} onRun={() => runOne.mutate(src.id)} busy={runOne.isPending} />
          ))}
          {sources.length === 0 && (
            <BentoCard className="col-span-full text-center">
              <p className="text-zinc-500">No sources configured yet.</p>
            </BentoCard>
          )}
        </div>
      )}
    </div>
  );
}

function SourceCard({ src, onRun, busy }) {
  const enabled = src.enabled !== false;
  const last = src.last_run;

  return (
    <BentoCard data-testid={SOURCES.card(src.id)} accent={enabled} className="min-h-[240px] flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Hexagon
            size={40}
            strokeWidth={2}
            className={enabled ? "text-yellow-500 fill-yellow-100" : "text-zinc-400 fill-zinc-100"}
          />
          <div>
            <div className="font-display text-lg font-bold leading-tight">{src.jurisdiction || src.id}</div>
            <div className="font-mono-data text-xs text-zinc-500 mt-1 flex items-center gap-1">
              <MapPin size={12} /> {src.state || "—"} {src.city_hint ? `· ${src.city_hint}` : ""}
            </div>
          </div>
        </div>
        {enabled ? <HexBadge tone="success">Enabled</HexBadge> : <HexBadge>Disabled</HexBadge>}
      </div>

      <div className="mt-4 space-y-2 text-sm">
        <Row label="ID" value={<span className="font-mono-data">{src.id}</span>} />
        <Row label="Adapter" value={<span className="font-mono-data">{src.adapter}</span>} />
        {src.source_url ? (
          <Row
            label="Source"
            value={
              <a
                href={src.source_url}
                target="_blank"
                rel="noreferrer"
                className="font-mono-data text-yellow-700 hover:underline inline-flex items-center gap-1 truncate"
              >
                {src.source_url.replace(/^https?:\/\//, "").slice(0, 32)}
                <ExternalLink size={12} />
              </a>
            }
          />
        ) : null}
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-200 text-sm">
        <div className="data-label mb-2">Last Run</div>
        {last ? (
          <div className="flex items-center gap-3">
            {last.ok ? (
              <CheckCircle2 size={16} className="text-emerald-600" />
            ) : (
              <XCircle size={16} className="text-red-500" />
            )}
            <span className="font-mono-data text-xs text-zinc-600">
              {fmtDateTime(last.finished_at)}
            </span>
            <span className="ml-auto font-display font-bold">{last.record_count ?? 0} recs</span>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">No runs yet.</p>
        )}
      </div>

      <div className="mt-auto pt-4">
        <BrutalButton
          data-testid={SOURCES.runBtn(src.id)}
          onClick={onRun}
          disabled={busy || !enabled}
          className="w-full justify-center"
          variant={enabled ? "primary" : "secondary"}
        >
          <Play size={14} strokeWidth={2.5} />
          Run Scraper
        </BrutalButton>
      </div>
    </BentoCard>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="data-label">{label}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}
